import { useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { fetchOrders } from 'actions/OrderActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { haveOrdersBeenFetched, isItemCancelled } from 'lib/order/orderUtils'
import noop from 'lib/function/noop'
import { getUpcomingOrders } from 'selectors/orderSelectors'
import ChatLandingExperienceOrderTile from 'tripPlanner/pages/chat/ChatLandingPage/ChatLandingExperienceOrderTile'
import ChatLandingFlightOrderTile from 'tripPlanner/pages/chat/ChatLandingPage/ChatLandingFlightOrderTile'
import ChatLandingUpcomingOrderTile from 'tripPlanner/pages/chat/ChatLandingPage/ChatLandingUpcomingOrderTile'

interface BaseAddedBookingTileData {
  orderId: string
  itemId: string
}

export interface AddedHotelBookingTileData extends BaseAddedBookingTileData {
  bookingType: 'hotel'
  offerId?: string
  propertyName: string
  checkInDate?: string
  checkOutDate?: string
  imageId?: string
  occupancy?: App.Occupants
}

export interface AddedExperienceBookingTileData extends BaseAddedBookingTileData {
  bookingType: 'experience'
  offerId: string
  propertyName: string
  checkInDate?: string
  time?: string
}

export interface AddedFlightBookingTileData extends BaseAddedBookingTileData {
  bookingType: 'flight'
}

export type AddedBookingTileData =
  | AddedHotelBookingTileData
  | AddedExperienceBookingTileData
  | AddedFlightBookingTileData

const CONTEXT_BLOCK_REGEX = /<Context(?:\s+label="[^"]*")?>([\s\S]*?)<\/Context>/gi

const TilesViewport = styled.div`
  align-self: flex-end;
  width: 100%;
  max-width: ${rem(740)};
`

const TilesContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${rem(10)};
  width: 100%;
`

function parseKeyValueLines(lines: Array<string>): Record<string, string> {
  return lines.reduce<Record<string, string>>((fields, line) => {
    const separatorIndex = line.indexOf(':')
    if (separatorIndex < 0) {
      return fields
    }

    const key = line.slice(0, separatorIndex).trim().toLowerCase()
    const value = line.slice(separatorIndex + 1).trim()
    if (!key || !value) {
      return fields
    }

    return {
      ...fields,
      [key]: value,
    }
  }, {})
}

function parseStructuredBookingSection(
  section: string,
): AddedBookingTileData | undefined {
  const lines = section
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)

  if (lines.length === 0) {
    return undefined
  }

  const heading = lines[0]
  const fields = parseKeyValueLines(lines.slice(1))
  const orderId = fields['order id']
  const itemId = fields['booking item id']

  if (!orderId || !itemId) {
    return undefined
  }

  switch (heading) {
    case 'Hotel booking':
      if (!fields.property) {
        return undefined
      }

      return {
        bookingType: 'hotel',
        orderId,
        itemId,
        offerId: fields['offer id'],
        propertyName: fields.property,
        checkInDate: fields['check-in'],
        checkOutDate: fields['check-out'],
      }
    case 'Experience booking':
      if (!fields['offer id'] || !fields.experience) {
        return undefined
      }

      return {
        bookingType: 'experience',
        orderId,
        itemId,
        offerId: fields['offer id'],
        propertyName: fields.experience,
        checkInDate: fields.date,
        time: fields.time,
      }
    case 'Flight booking':
      return {
        bookingType: 'flight',
        orderId,
        itemId,
      }
    default:
      return undefined
  }
}

function parseBookedFlightContextBlock(
  contextBlock: string,
): AddedFlightBookingTileData | undefined {
  const fields = parseKeyValueLines(
    contextBlock
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean),
  )

  const orderId = fields.order_id
  const itemId = fields.order_flight_item_id

  if (!orderId || !itemId) {
    return undefined
  }

  return {
    bookingType: 'flight',
    orderId,
    itemId,
  }
}

function addTileIfMissing(
  tiles: Array<AddedBookingTileData>,
  seenTileKeys: Set<string>,
  tile: AddedBookingTileData | undefined,
): void {
  if (!tile) {
    return
  }

  const tileKey = `${tile.bookingType}:${tile.orderId}:${tile.itemId}`
  if (seenTileKeys.has(tileKey)) {
    return
  }

  seenTileKeys.add(tileKey)
  tiles.push(tile)
}

export function extractAddedBookingTilesFromMessage(
  message: string | undefined,
): Array<AddedBookingTileData> {
  if (!message) {
    return []
  }

  const tiles: Array<AddedBookingTileData> = []
  const seenTileKeys = new Set<string>()
  const contextBlocks = message.matchAll(CONTEXT_BLOCK_REGEX)

  for (const contextBlock of contextBlocks) {
    const blockContent = contextBlock[1]?.trim()
    if (!blockContent) {
      continue
    }

    addTileIfMissing(
      tiles,
      seenTileKeys,
      parseBookedFlightContextBlock(blockContent),
    )

    const sections = blockContent
      .split(/\n\s*\n+/)
      .map(section => section.trim())
      .filter(Boolean)

    for (const section of sections) {
      addTileIfMissing(
        tiles,
        seenTileKeys,
        parseStructuredBookingSection(section),
      )
    }
  }

  return tiles
}

function hydrateHotelBookingTile(
  tile: AddedHotelBookingTileData,
  orders: Array<App.Order>,
): AddedHotelBookingTileData {
  const matchingOrder = orders.find(order => order.id === tile.orderId)
  if (!matchingOrder) {
    return tile
  }

  const matchingOrderItem = matchingOrder.items.find(
    item => !isItemCancelled(item) && item.id === tile.itemId,
  )

  if (matchingOrderItem) {
    const occupancy = tile.occupancy ?? (matchingOrderItem.reservation ? {
      adults: matchingOrderItem.reservation.adults,
      children: matchingOrderItem.reservation.children,
      infants: matchingOrderItem.reservation.infants,
    } : undefined)

    return {
      ...tile,
      offerId: tile.offerId ?? matchingOrderItem.offerId,
      propertyName:
        matchingOrderItem.offer.property?.name ??
        matchingOrderItem.offer.name ??
        tile.propertyName,
      checkInDate: tile.checkInDate ?? matchingOrderItem.reservation?.startDate,
      checkOutDate: tile.checkOutDate ?? matchingOrderItem.reservation?.endDate,
      imageId:
        tile.imageId ??
        matchingOrderItem.offer.property?.roomType?.images?.[0]?.id,
      occupancy,
    }
  }

  const matchingBedbankItem = matchingOrder.bedbankItems.find(
    item => !isItemCancelled(item) && item.id === tile.itemId,
  )

  if (matchingBedbankItem) {
    return {
      ...tile,
      offerId: tile.offerId ?? matchingBedbankItem.offer.id,
      propertyName:
        matchingBedbankItem.offer.property?.name ??
        matchingBedbankItem.offer.name ??
        tile.propertyName,
      imageId: tile.imageId ?? matchingBedbankItem.offer.image?.id,
      occupancy: tile.occupancy ?? matchingBedbankItem.rooms[0]?.occupancy,
    }
  }

  return tile
}

export function hydrateAddedBookingTiles(
  tiles: Array<AddedBookingTileData>,
  orders: Array<App.Order>,
): Array<AddedBookingTileData> {
  return tiles.map((tile) => {
    if (tile.bookingType !== 'hotel') {
      return tile
    }

    return hydrateHotelBookingTile(tile, orders)
  })
}

interface Props {
  tiles: Array<AddedBookingTileData>
}

function AddedBookingTiles({ tiles }: Props) {
  const dispatch = useAppDispatch()
  const upcomingOrders = useAppSelector(getUpcomingOrders)
  const haveFetchedUpcomingOrders = useAppSelector(state =>
    haveOrdersBeenFetched(state, 'upcoming'),
  )
  const hasHotelTiles = useMemo(
    () => tiles.some(tile => tile.bookingType === 'hotel'),
    [tiles],
  )
  const hydratedTiles = useMemo(
    () => hydrateAddedBookingTiles(tiles, upcomingOrders),
    [tiles, upcomingOrders],
  )

  useEffect(() => {
    if (!hasHotelTiles || haveFetchedUpcomingOrders) {
      return
    }

    dispatch(fetchOrders({ status: 'upcoming' }))
  }, [dispatch, hasHotelTiles, haveFetchedUpcomingOrders])

  if (tiles.length === 0) {
    return null
  }

  return (
    <TilesViewport>
      <TilesContainer>
        {hydratedTiles.map((tile) => {
          const key = `${tile.bookingType}:${tile.orderId}:${tile.itemId}`

          switch (tile.bookingType) {
            case 'flight':
              return (
                <ChatLandingFlightOrderTile
                  key={key}
                  itemId={tile.itemId}
                  orderId={tile.orderId}
                  displayVariant="addedToTrip"
                  onClick={noop}
                  disabled
                />
              )
            case 'experience':
              return (
                <ChatLandingExperienceOrderTile
                  key={key}
                  itemId={tile.itemId}
                  experienceId={tile.offerId}
                  propertyName={tile.propertyName}
                  checkInDate={tile.checkInDate}
                  time={tile.time}
                  displayVariant="addedToTrip"
                  onClick={noop}
                  disabled
                />
              )
            case 'hotel':
              return (
                <ChatLandingUpcomingOrderTile
                  key={key}
                  itemId={tile.itemId}
                  propertyName={tile.propertyName}
                  checkInDate={tile.checkInDate}
                  checkOutDate={tile.checkOutDate}
                  imageId={tile.imageId}
                  occupancy={tile.occupancy}
                  displayVariant="addedToTrip"
                  onClick={noop}
                  disabled
                />
              )
            default:
              return null
          }
        })}
      </TilesContainer>
    </TilesViewport>
  )
}

export default AddedBookingTiles
